/* eslint-env browser */
// Complaints (wave 3, W2_Portal_Tables look): the complaints in the modern
// table, newest first, with Pending / Processed chips (with their counts)
// above it; a row's button opens the complaint, where staff add the notes
// and mark it processed (or read a processed one).
//
// Data, exactly as before: the complaints list already in the store
// (complaindata.list); nothing new is read. "Mark as processed" calls the
// same editComplain({ ...complaint, check: true, processedBy, processDate,
// comments }, 'Update'). The row passed is the plain record (material-table
// used to add its own `tableData` key to it, which was then saved too).
// Email and phone are hidden in demo mode; exports for admins (CSV only with
// critical edits on), as before.
// Call-centre feedback (29 Oct 2026): a copy button beside each number copies
// it without spaces (+251964540448) for MicroSIP; it never opens the row and
// is not there while numbers are hidden.
import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { tokens as c } from '../theme';
import { showToast } from '../components/feedback';
import { formatAddisDateTime } from '../common/exportUtils';
import ClickableUserLink from '../components/ClickableUserLink';
import { CopyButton, DataTable, ChipGroup, ActiveFilters, StatusChip, InfoRow, FieldLabel, SrOnly, ALL, calmCaps, phoneMatches } from '../components/modern';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

export default function Complain() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const { editComplain } = api;
  const complaindata = useSelector((state) => state.complaindata.list);
  const complainLoading = useSelector((state) => state.complaindata.loading);
  const dispatch = useDispatch();
  // Removed dispatch(fetchUsersOnce()) — was downloading the entire users
  // collection on every Complain page mount JUST to populate tooltips on
  // ClickableUserLink. The link now navigates directly from the userId
  // already on each complaint row, so no users data is needed here.
  const data = useMemo(() => (Array.isArray(complaindata) ? complaindata : []), [complaindata]);

  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const hidden = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const [status, setStatus] = useState(ALL); // 'all' | 'pending' | 'processed'

  const counts = useMemo(() => {
    let processed = 0;
    data.forEach((row) => {
      if (row.check) processed += 1;
    });
    return { all: data.length, processed, pending: data.length - processed };
  }, [data]);

  const rows = useMemo(() => {
    if (status === 'pending') return data.filter((row) => !row.check);
    if (status === 'processed') return data.filter((row) => !!row.check);
    return data;
  }, [data, status]);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedComplain, setSelectedComplain] = useState(null);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleProcessComplain = (rowData) => {
    setSelectedComplain(rowData);
    setComments(rowData.comments || '');
    setOpenDialog(true);
  };

  const handleConfirmProcess = async () => {
    if (!comments.trim()) {
      showToast.error(t('comments_required'));
      return;
    }

    setProcessing(true);

    // Update complaint with processing information
    const updatedComplain = {
      ...selectedComplain,
      check: true,
      processedBy: auth.profile && auth.profile.email ? auth.profile.email : auth.profile.uid,
      processDate: new Date().getTime(),
      comments: comments.trim(),
    };

    // Process the complaint
    dispatch(editComplain(updatedComplain, 'Update'));

    setProcessing(false);
    setOpenDialog(false);
    setComments('');
    setSelectedComplain(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setComments('');
    setSelectedComplain(null);
  };

  const statusLabel = (row) => (row.check ? t('processed') : t('pending'));
  const userType = (row) => (row.role === 'driver' ? 'driver' : row.role === 'customer' ? 'customer' : 'auto');

  const columns = [
    { id: 'complainDate', title: t('createdAt'), field: 'complainDate', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'firstName',
      title: t('first_name'),
      field: 'firstName',
      render: (rowData) => {
        const fullName = `${rowData.firstName || ''} ${rowData.lastName || ''}`.trim();
        return fullName ? (
          <ClickableUserLink
            value={rowData.firstName}
            type={userType(rowData)}
            searchField="name"
            userId={rowData.uid}
            iconType={rowData.role === 'driver' ? 'car' : 'person'}
          />
        ) : (
          '-'
        );
      },
    },
    { id: 'lastName', title: t('last_name'), field: 'lastName', render: (rowData) => rowData.lastName || '-' },
    { id: 'role', title: t('usertype'), field: 'role', searchValue: (row) => (row.role ? `${row.role} ${t(row.role)}` : ''), render: (row) => (row.role ? t(row.role, { defaultValue: row.role }) : '-') },
    {
      id: 'email',
      title: t('email'),
      field: 'email',
      sortValue: (row) => (allowPrivate ? row.email || '' : ''),
      render: (rowData) => (allowPrivate ? rowData.email || '-' : hidden),
    },
    {
      id: 'mobile',
      title: t('mobile'),
      field: 'mobile',
      nowrap: true,
      sortValue: (row) => (allowPrivate ? row.mobile || '' : ''),
      render: (rowData) =>
        allowPrivate ? (
          rowData.mobile ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
              <ClickableUserLink value={rowData.mobile} type={userType(rowData)} searchField="phone" userId={rowData.uid} iconType="phone" />
              <CopyButton value={rowData.mobile} phone what={phoneWord} sx={{ my: -0.5 }} />
            </Box>
          ) : (
            '-'
          )
        ) : (
          hidden
        ),
    },
    { id: 'subject', title: t('subject'), field: 'subject', minWidth: 160 },
    {
      id: 'check',
      title: t('status'),
      field: 'check',
      sortValue: (row) => (row.check ? 1 : 0),
      render: (rowData) => (
        <StatusChip
          tone={rowData.check ? 'success' : 'warning'}
          icon={rowData.check ? <CheckRoundedIcon /> : <ScheduleRoundedIcon />}
          label={statusLabel(rowData)}
        />
      ),
    },
    { id: 'processedBy', title: t('processedBy'), field: 'processedBy' },
    { id: 'processDate', title: t('processDate'), field: 'processDate', type: 'datetime' },
    {
      // Open the complaint: "Process complaint" while pending, "View details" once processed.
      id: 'open',
      title: t('action'),
      hideable: false,
      sortable: false,
      searchable: false,
      export: false,
      render: (rowData) =>
        rowData.check ? (
          <Button variant="text" size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => handleProcessComplain(rowData)} sx={{ whiteSpace: 'nowrap' }}>
            {t('view_details')}
          </Button>
        ) : (
          <Button variant="outlined" color="neutral" size="small" startIcon={<CheckCircleOutlineRoundedIcon />} onClick={() => handleProcessComplain(rowData)} sx={{ whiteSpace: 'nowrap' }}>
            {t('process_complaint')}
          </Button>
        ),
    },
    {
      id: 'comments',
      title: t('notes'),
      field: 'comments',
      render: (rowData) =>
        rowData.comments ? (
          <Tooltip title={rowData.comments}>
            <Box component="span" tabIndex={0} sx={{ maxWidth: 220, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              {rowData.comments}
            </Box>
          </Tooltip>
        ) : null,
    },
  ];

  // The same CSV columns as before; dates in Addis Ababa 12-hour time.
  const exportColumns = [
    { title: t('createdAt'), field: 'complainDate', type: 'datetime' },
    { title: t('first_name'), field: 'firstName' },
    { title: t('last_name'), field: 'lastName' },
    { title: t('usertype'), field: 'role' },
    { title: t('email'), value: (row) => (allowPrivate ? row.email || '' : hidden) },
    { title: t('mobile'), value: (row) => (allowPrivate ? row.mobile || '' : hidden) },
    { title: t('subject'), field: 'subject' },
    { title: t('status'), value: statusLabel },
    { title: t('processedBy'), field: 'processedBy' },
    { title: t('processDate'), field: 'processDate', type: 'datetime' },
    { title: t('notes'), field: 'comments' },
  ];

  // Name, subject, notes and type always; email and phone only when shown.
  const searchFn = (row, q, ql) => {
    const text = [row.firstName, row.lastName, row.subject, row.comments, row.processedBy, row.role, row.role ? t(row.role) : '', statusLabel(row)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    if (text.includes(ql)) return true;
    if (!allowPrivate) return false;
    if (phoneMatches(row.mobile, q)) return true;
    return String(row.email || '').toLowerCase().includes(ql);
  };

  const statusOptions = [
    { value: ALL, label: t('all', { defaultValue: 'All' }), count: counts.all },
    { value: 'pending', label: t('pending'), count: counts.pending },
    { value: 'processed', label: t('processed'), count: counts.processed },
  ];
  const statusGroup = t('status');
  const tokens = status !== ALL ? [{ key: 'status', group: statusGroup, value: (statusOptions.find((o) => o.value === status) || {}).label }] : [];
  const title = calmCaps(t('complain_title', { defaultValue: 'Complaints' }));
  const loading = !complaindata && !!complainLoading;
  const isAdmin = auth.profile.usertype === 'admin';
  const readOnly = !!(selectedComplain && selectedComplain.check);

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <ChipGroup label={statusGroup} value={status} options={statusOptions} onChange={setStatus} />
      </Paper>

      <DataTable
        title={title}
        rows={rows}
        columns={columns}
        loading={loading}
        countText={({ shown, total, query }) =>
          query || status !== ALL
            ? t('table_count_shown', { shown: fmt(query ? shown : total), total: fmt(counts.all), defaultValue: '{{shown}} of {{total}}' })
            : t('complaints_count', {
                count: total,
                n: fmt(total),
                defaultValue_one: '{{n}} complaint',
                defaultValue_other: '{{n}} complaints',
                defaultValue: '{{n}} complaints',
              })
        }
        headerContent={tokens.length ? <ActiveFilters tokens={tokens} onRemove={() => setStatus(ALL)} onClearAll={() => setStatus(ALL)} /> : null}
        searchFn={searchFn}
        resortKey={allowPrivate ? 'private' : 'demo'}
        onRowClick={handleProcessComplain}
        rowLabel={(row) => `${row.firstName || ''} ${row.lastName || ''}`.trim()}
        rowSx={(row) => (row.check ? null : { '& > td': { fontWeight: 600 } })}
        prefsKey="complaints"
        exportCsv={isAdmin && allowPrivate}
        exportPrint={isAdmin}
        exportColumns={exportColumns}
        exportFileName="complaints"
        exportTitle={title}
        minWidth={1320}
        emptyState={({ query }) =>
          query
            ? null
            : status !== ALL
              ? {
                  icon: <FilterAltOffOutlinedIcon />,
                  title: t('complaints_empty_filtered', { defaultValue: 'No complaints with this status' }),
                  actionLabel: t('clear_filters', { defaultValue: 'Clear filters' }),
                  onAction: () => setStatus(ALL),
                }
              : { icon: <FeedbackOutlinedIcon />, title: t('complaints_empty', { defaultValue: 'No complaints yet' }) }
        }
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth fullScreen={fullScreen} aria-labelledby="complaint-dialog-title">
        <DialogTitle id="complaint-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1.5 }}>
          <Box component="span" sx={{ flex: 1, fontSize: 18, fontWeight: 700, color: c.INK }}>
            {readOnly ? t('complaint_details') : t('process_complaint_title')}
          </Box>
          <IconButton onClick={handleCloseDialog} aria-label={t('close_dialog', { defaultValue: 'Close' })} disabled={processing}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedComplain ? (
            <Box component="dl" sx={{ m: 0, mb: 2 }}>
              <InfoRow label={t('name')}>{`${selectedComplain.firstName || ''} ${selectedComplain.lastName || ''}`.trim()}</InfoRow>
              <InfoRow label={t('usertype')}>{selectedComplain.role ? t(selectedComplain.role, { defaultValue: selectedComplain.role }) : null}</InfoRow>
              <InfoRow label={t('email')}>{allowPrivate ? selectedComplain.email : hidden}</InfoRow>
              <InfoRow label={t('mobile')}>
                {allowPrivate ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                    {selectedComplain.mobile}
                    <CopyButton value={selectedComplain.mobile} phone what={phoneWord} sx={{ my: -0.5 }} />
                  </Box>
                ) : (
                  hidden
                )}
              </InfoRow>
              <InfoRow label={t('subject')}>{selectedComplain.subject}</InfoRow>
              <InfoRow label={t('message_text')} alignTop>
                <Typography component="span" sx={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                  {selectedComplain.body}
                </Typography>
              </InfoRow>
              <InfoRow label={t('complain_date')}>{formatAddisDateTime(selectedComplain.complainDate)}</InfoRow>
              {selectedComplain.check ? (
                <>
                  <InfoRow label={t('processedBy')}>{selectedComplain.processedBy}</InfoRow>
                  <InfoRow label={t('processDate')}>{formatAddisDateTime(selectedComplain.processDate)}</InfoRow>
                </>
              ) : null}
            </Box>
          ) : null}
          <FieldLabel htmlFor="complaint-notes">{t('notes_comments')}</FieldLabel>
          <TextField
            id="complaint-notes"
            autoFocus={!readOnly}
            fullWidth
            multiline
            minRows={4}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            disabled={processing || readOnly}
            inputProps={{ 'aria-describedby': 'complaint-notes-hint' }}
          />
          <SrOnly id="complaint-notes-hint">{readOnly ? t('notes_readonly') : t('complaint_notes_help')}</SrOnly>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button variant="text" color="neutral" onClick={handleCloseDialog} disabled={processing}>
            {readOnly ? calmCaps(t('close')) : t('cancel')}
          </Button>
          {!readOnly ? (
            <Button onClick={handleConfirmProcess} variant="contained" startIcon={<CheckRoundedIcon />} disabled={processing || !comments.trim()}>
              {processing ? t('form_saving', { defaultValue: 'Saving…' }) : t('mark_as_processed')}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
